import React, { useContext, useEffect, useState } from "react";

import { useMutation } from "@apollo/client";
import {
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FileCopy as FileCopyIcon,
  RestaurantMenu as RecipeIcon,
} from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  InputBase,
  List,
  Tooltip,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import AddFoodPortionDialog from "@/components/FoodDatabase/AddFoodPortionDialog";
import AddMealPatternDialog from "@/components/FoodDatabase/AddMealPatternDialog";
import InfoIcon from "@/components/Icons/InfoIcon";
import { MealPatternNameKind } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import useKeyboardShortcut from "@/hooks/useKeyboardShortcut";
import { createRecipeMutation } from "@/pages/Database/Recipes/store/RecipeStore";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import { stopPropagation } from "@/utils/functions";
import { round } from "@/utils/number";

import OverwriteCellDialog from "./Dialogs/OverwriteCellDialog";
import MealItemMacros from "./MealItemMacros";
import MealPatternItemView from "./MealPatternItemView";
import PlannedMealItemsCellItem from "./PlannedMealItemsCellItem";
import { Cell } from "../store/Cell";
import { CellRecipe } from "../store/CellRecipe";
import MealPatternItem from "../store/MealPatternItem";
import MealPlanFormStore from "../store/MealPlanFormStore";
import { PlannedMealItem } from "../store/PlannedMealItem";

interface Props {
  className?: string;
  cell: Cell;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "stretch",
    padding: theme.spacing(2),
    cursor: "default",
    border: "2px solid #fafafa",
    backgroundColor: colors.white,
  },
  active: {
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: `0px 0px 40px rgba(17, 35, 64, 0.25)`,
  },
  iconButton: {
    marginLeft: theme.spacing(3),
    color: "#C0C5CD",
  },
  paste: {
    color: theme.palette.primary.main,
  },
  deleteActive: {
    color: "#FF6262",
  },
  cellRecipe: {
    borderRadius: theme.spacing(1),
    backgroundColor: colors.light,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  infoIcon: {
    width: 12,
    height: 12,
    color: colors.gray,
  },
  tooltipArrow: {
    color: colors.dark,
  },
  tooltipTooltip: {
    backgroundColor: colors.dark,
    borderRadius: 0,
  },
  tooltipText: {
    fontSize: 12,
    lineHeight: "14px",
    fontWeight: 600,
    color: colors.white,
  },
  tooltipTextValue: {
    color: theme.palette.primary.main,
  },
}));

/**
 * One named group of foods inside a cell: collapsible header with an editable
 * name, tweakable ingredients, and — for groups not yet backed by a library
 * recipe — a "Save to My Recipes" action.
 */
const CellRecipeSection: React.FC<{
  cellRecipe: CellRecipe;
  savingRecipe: boolean;
  isDeleting: boolean;
  onEditItem: (item: PlannedMealItem) => void;
  onSaveToRecipes: (cellRecipe: CellRecipe) => void;
}> = observer(
  ({ cellRecipe, savingRecipe, isDeleting, onEditItem, onSaveToRecipes }) => {
    const { classes } = useStyles();
    const [nameDraft, setNameDraft] = useState(cellRecipe.name);

    useEffect(() => setNameDraft(cellRecipe.name), [cellRecipe.name]);

    function preventRipple(e: React.MouseEvent) {
      e.stopPropagation();
    }

    function commitName() {
      if (nameDraft.trim()) {
        cellRecipe.setName(nameDraft);
      } else {
        setNameDraft(cellRecipe.name);
      }
    }

    return (
      <Box className={classes.cellRecipe} onMouseDown={preventRipple}>
        <Box display="flex" alignItems="center" gap={0.5}>
          {cellRecipe.isLinked ? (
            <Tooltip title="Saved in My Recipes" placement="top">
              <RecipeIcon fontSize="small" color="primary" />
            </Tooltip>
          ) : (
            <Tooltip title="Save to My Recipes" placement="top">
              <span>
                <IconButton
                  size="small"
                  aria-label="Save to My Recipes"
                  disabled={savingRecipe}
                  sx={{
                    padding: 0,
                    width: 20,
                    height: 20,
                    color: colors.gray,
                  }}
                  onClick={stopPropagation(() => onSaveToRecipes(cellRecipe))}
                >
                  <RecipeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <InputBase
            value={nameDraft}
            fullWidth
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            inputProps={{ "aria-label": "Group name" }}
            sx={{ fontWeight: 600, fontSize: 13 }}
          />
          <Typography
            variant="caption"
            color="textSecondary"
            whiteSpace="nowrap"
          >
            {Math.round(cellRecipe.totalEnergyKcal)} kcal
          </Typography>
          <Tooltip
            arrow
            classes={{
              arrow: classes.tooltipArrow,
              tooltip: classes.tooltipTooltip,
            }}
            title={
              <Typography className={classes.tooltipText}>
                Proteins :{" "}
                <span className={classes.tooltipTextValue}>
                  {round(cellRecipe.totalProteinGrams, 2)}g
                </span>
                , Fats :{" "}
                <span className={classes.tooltipTextValue}>
                  {round(cellRecipe.totalFatGrams, 2)}g
                </span>
                ,<br /> Carbohydrates :{" "}
                <span className={classes.tooltipTextValue}>
                  {round(cellRecipe.totalCarbohydrateGrams, 2)}g
                </span>
              </Typography>
            }
          >
            <InfoIcon className={classes.infoIcon} />
          </Tooltip>
          <Tooltip
            title={
              cellRecipe.collapsed ? "Show ingredients" : "Hide ingredients"
            }
            placement="top"
          >
            <IconButton
              size="small"
              aria-label={
                cellRecipe.collapsed ? "Show ingredients" : "Hide ingredients"
              }
              onClick={stopPropagation(() => cellRecipe.toggleCollapsed())}
            >
              {cellRecipe.collapsed ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ExpandLessIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          {isDeleting && (
            <Tooltip title="Remove group" placement="top">
              <IconButton
                size="small"
                aria-label="Remove group"
                onClick={stopPropagation(() => cellRecipe.remove())}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {!cellRecipe.collapsed && (
          <List dense disablePadding>
            {cellRecipe.items.map((item, index) => (
              <React.Fragment key={item.uuid}>
                <PlannedMealItemsCellItem item={item} onEdit={onEditItem} />
                {index !== cellRecipe.items.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    );
  }
);

const PlannedMealItemsCell: React.FC<Props> = observer(
  ({ className, cell }) => {
    const { classes, cx } = useStyles();
    const store = useContext(MealPlanFormStore.Context);
    const clipboard = store.clipboard;
    const canCopy = !clipboard.isCopying && !cell.isEmpty;
    const canPaste =
      clipboard.mode === "cell" &&
      clipboard.copiedCell !== cell &&
      clipboard.copiedCell?.mode === cell.mode;
    const isDeleting = cell.isDeleting;
    const overwriteCellDialog = useDialogState();
    const [editItem, setEditItem] = useState<PlannedMealItem>();
    const [editMealPatternItem, setEditMealPatternItem] =
      useState<MealPatternItem>();

    const snackbar = useGlobalSnackbar();
    const [createRecipe, { loading: savingRecipe }] =
      useMutation(createRecipeMutation);
    const [looseNameDraft, setLooseNameDraft] = useState("");

    function preventRipple(e: React.MouseEvent) {
      e.stopPropagation();
    }

    /** Wrap the loose foods into a named, local-only group. */
    function nameLooseItems() {
      const name = looseNameDraft.trim();
      if (!name || cell.looseItems.length === 0) return;
      cell.groupLooseItemsIntoCellRecipe(name);
      setLooseNameDraft("");
    }

    /** Promote a local group to a recipe in the coach's library. */
    async function saveGroupToRecipes(cellRecipe: CellRecipe) {
      try {
        const { data } = await createRecipe({
          variables: {
            input: {
              attributes: {
                name: cellRecipe.name,
                description: "",
                preparationMinutes: 1,
                numberOfPortions: 1,
                ingredientsAttributes: cellRecipe.items.map((item) => ({
                  foodId: item.food.id,
                  foodPortionId: item.foodPortion.id,
                  amount: item.amount,
                  _destroy: false,
                })),
              },
            },
          },
        });
        const recipe = data?.createRecipe?.recipe;
        if (recipe) {
          cellRecipe.linkRecipe(recipe.id, recipe.name);
          snackbar.open(`Saved "${recipe.name}" to your recipes.`);
        }
      } catch (e) {
        snackbar.open(displayError(e, "Unable to save the group as a recipe"));
      }
    }

    function pasteClipboard() {
      if (!cell.isEmpty) {
        overwriteCellDialog.show();
      } else {
        confirmPasteClipboard();
      }
    }

    function confirmPasteClipboard() {
      clipboard.pasteCell(cell);
    }

    useKeyboardShortcut("Ctrl+C", () => {
      if (cell.isActive) clipboard.copyCell(cell);
    });

    useKeyboardShortcut("Ctrl+V", () => {
      if (cell.isActive) pasteClipboard();
    });

    useKeyboardShortcut("Ctrl+X", () => {
      if (cell.isActive) {
        clipboard.copyCell(cell);
        cell.clearItems();
      }
    });

    const isPlannedMealItemsEmpty = isEmpty(cell.plannedMealItems);
    const isMealPatternItemsEmpty = isEmpty(cell.mealPatternItems);
    const isFoodMode = cell.mode === "planned_meal_item";
    const hasLooseItems = isFoodMode && cell.looseItems.length > 0;
    return (
      <>
        <ButtonBase
          component="div"
          className={cx(
            classes.root,
            className,
            cell.isActive && classes.active
          )}
          TouchRippleProps={{}}
          onClick={() => store.cells.setActive(cell)}
          data-cell={cell.key}
        >
          {isFoodMode && (
            <div>
              {cell.cellRecipes.map((cellRecipe) => (
                <CellRecipeSection
                  key={cellRecipe.uuid}
                  cellRecipe={cellRecipe}
                  savingRecipe={savingRecipe}
                  isDeleting={isDeleting}
                  onEditItem={setEditItem}
                  onSaveToRecipes={(cr) => void saveGroupToRecipes(cr)}
                />
              ))}
            </div>
          )}
          {hasLooseItems && (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              paddingBottom={1}
              onClick={preventRipple}
              onMouseDown={preventRipple}
            >
              <RecipeIcon fontSize="small" sx={{ color: colors.gray }} />
              <InputBase
                value={looseNameDraft}
                placeholder="Name these foods…"
                fullWidth
                onChange={(e) => setLooseNameDraft(e.target.value)}
                onBlur={nameLooseItems}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                inputProps={{ "aria-label": "Name these foods" }}
                sx={{ fontWeight: 600, fontSize: 13 }}
              />
            </Box>
          )}
          <List>
            {isFoodMode
              ? cell.looseItems.map((item, index) => (
                  <React.Fragment key={item.uuid}>
                    <PlannedMealItemsCellItem
                      item={item}
                      onEdit={setEditItem}
                    />
                    {index !== cell.looseItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              : cell.mealPatternItems.map((item, index) => (
                  <React.Fragment key={item.uuid}>
                    <MealPatternItemView
                      item={item}
                      onEdit={setEditMealPatternItem}
                    />
                    {index !== cell.mealPatternItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
            {!isPlannedMealItemsEmpty && (
              <MealItemMacros items={cell.plannedMealItems} />
            )}
            {!isMealPatternItemsEmpty && (
              <MealItemMacros items={cell.mealPatternItems} />
            )}
          </List>
          <div>
            <Divider style={{ borderColor: colors.light }} />
            <Box
              width="100%"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              paddingTop={2}
            >
              <Typography variant="subtitle1" color="initial">
                {Math.round(cell.totalEnergyKcal)} kcal
              </Typography>
              <Box display="flex" alignItems="center">
                {canCopy && (
                  <Tooltip title="Copy" placement="top">
                    <IconButton
                      className={classes.iconButton}
                      size="small"
                      onMouseDown={preventRipple}
                      onClick={stopPropagation(() => clipboard.copyCell(cell))}
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canPaste && (
                  <Tooltip title="Paste" placement="top">
                    <IconButton
                      className={cx(classes.iconButton, classes.paste)}
                      size="small"
                      onMouseDown={preventRipple}
                      onClick={stopPropagation(pasteClipboard)}
                    >
                      <AssignmentIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {cell.items.length > 0 && (
                  <Tooltip title="Delete" placement="top">
                    <IconButton
                      className={cx(classes.iconButton, {
                        [classes.deleteActive]: isDeleting,
                      })}
                      size="small"
                      onMouseDown={preventRipple}
                      onClick={stopPropagation(() => cell.toggleDeleting())}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </div>
        </ButtonBase>
        <OverwriteCellDialog
          cell={cell}
          open={overwriteCellDialog.shown}
          onClose={overwriteCellDialog.hide}
          onConfirm={confirmPasteClipboard}
        />
        <AddFoodPortionDialog
          open={!!editItem}
          foodId={editItem?.food.id ?? ""}
          defaultFoodPortionId={editItem?.foodPortion.id}
          defaultAmount={editItem?.amount}
          title="Edit Food Portions"
          onClose={() => setEditItem(undefined)}
          onAddFood={(_, foodPortion, amount) => {
            if (!editItem) return;
            editItem.setFoodPortion(foodPortion);
            editItem.setAmount(amount);
          }}
        />
        <AddMealPatternDialog
          open={!!editMealPatternItem}
          food={editMealPatternItem?.food}
          foodportion={editMealPatternItem?.foodPortion}
          name={editMealPatternItem?.name ?? MealPatternNameKind.Fruits}
          defaultAmount={editMealPatternItem?.amountServings}
          onClose={() => setEditMealPatternItem(undefined)}
          onAddMealPattern={(_, amountServings) => {
            editMealPatternItem?.setAmount(amountServings);
          }}
        />
      </>
    );
  }
);

export default PlannedMealItemsCell;
